import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const POLL_INTERVAL_MS = 30000;

const SEV_CONFIG = {
  CRITICAL: { color: '#c0392b', bg: '#fdecea', icon: '🔴', border: '#e74c3c' },
  HIGH:     { color: '#e67e22', bg: '#fef5e7', icon: '🟠', border: '#f39c12' },
  MEDIUM:   { color: '#f1c40f', bg: '#fefde7', icon: '🟡', border: '#f1c40f' },
  LOW:      { color: '#27ae60', bg: '#eafaf1', icon: '🟢', border: '#27ae60' },
};

const SOURCE_LABELS = {
  LookoutForMetrics: { label: 'ML (Z-Score)',   icon: '🤖' },
  RuleEngine:        { label: 'Rule Engine',    icon: '📏' },
  StatisticalEngine: { label: 'Statistical',    icon: '📊' },
};

export default function AnomalyDashboard() {
  const [alerts,    setAlerts]    = useState([]);
  const [summary,   setSummary]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [filter,    setFilter]    = useState('ALL');
  const [lastPoll,  setLastPoll]  = useState(null);
  const [detectorStatus, setDetectorStatus] = useState('checking');

  const fetchAlerts = useCallback(async () => {
    try {
      const url = filter === 'ALL'
        ? `${API}/api/anomalies?limit=50&status=ALL`
        : `${API}/api/anomalies?limit=50&severity=${filter}&status=ALL`;
      const res  = await fetch(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load anomalies');
      setAlerts(data.alerts || []);
      setSummary(data.summary || {});
      setLastPoll(new Date());
      setError('');
      setDetectorStatus(data.alerts?.length >= 0 ? 'active' : 'warming');
    } catch (e) {
      setError(e.message);
      setDetectorStatus('error');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const activeAlerts  = alerts.filter(a => a.status === 'ACTIVE');
  const resolvedAlerts = alerts.filter(a => a.status !== 'ACTIVE');

  return (
    <div style={s.page}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div>
          <h2 style={s.heading}>🤖 AI Anomaly Detection</h2>
          <p style={s.sub}>
          Statistical Z-score + Rule Engine + Trend Analysis monitoring CycleTime, Temperature, ClampForce, OEE
          </p>
        </div>
        <div style={s.statusRow}>
          <DetectorStatus status={detectorStatus} />
          <button style={s.refreshBtn} onClick={fetchAlerts} disabled={loading}>
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      {/* ── Summary cards ── */}
      <div style={s.summaryRow}>
        {[
          ['CRITICAL', summary.critical || 0, '🔴'],
          ['HIGH',     summary.high     || 0, '🟠'],
          ['MEDIUM',   summary.medium   || 0, '🟡'],
          ['ALL',      summary.total    || 0, '📋'],
        ].map(([key, count, icon]) => {
          const cfg = SEV_CONFIG[key] || { bg: '#f0f4f8', border: '#ccc', color: '#333' };
          return (
            <div key={key} style={{
              ...s.summaryCard,
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              outline: filter === key ? `2px solid ${cfg.border}` : 'none',
              cursor: 'pointer',
            }} onClick={() => setFilter(filter === key ? 'ALL' : key)}>
              <span style={{ fontSize: 22 }}>{icon}</span>
              <span style={{ fontSize: 28, fontWeight: 700, color: cfg.color || '#333' }}>{count}</span>
              <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>{key}</span>
            </div>
          );
        })}
        <div style={s.pollInfo}>
          {lastPoll && <span style={{ fontSize: 11, color: '#888' }}>Last poll: {lastPoll.toLocaleTimeString()}</span>}
          <span style={{ fontSize: 11, color: '#aaa' }}>Auto-refresh 30s</span>
        </div>
      </div>

      {/* ── Metric health strip ── */}
      <MetricHealthStrip alerts={activeAlerts} />

      {/* ── Active alerts ── */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>
          🚨 Active Anomalies
          <span style={s.badge}>{activeAlerts.length}</span>
        </h3>
        {loading ? (
          <p style={s.empty}>Loading…</p>
        ) : activeAlerts.length === 0 ? (
          <div style={s.allClear}>
            <span style={{ fontSize: 32 }}>✅</span>
            <span>All metrics normal — no active anomalies detected</span>
          </div>
        ) : (
          <div style={s.alertList}>
            {activeAlerts.map(alert => <AlertCard key={alert.alertId} alert={alert} />)}
          </div>
        )}
      </div>

      {/* ── Resolved alerts ── */}
      {resolvedAlerts.length > 0 && (
        <div style={s.section}>
          <h3 style={{ ...s.sectionTitle, color: '#888' }}>
            ✅ Resolved / Historical
            <span style={{ ...s.badge, background: '#eee', color: '#666' }}>{resolvedAlerts.length}</span>
          </h3>
          <div style={s.alertList}>
            {resolvedAlerts.slice(0, 10).map(alert => <AlertCard key={alert.alertId} alert={alert} resolved />)}
          </div>
        </div>
      )}

      {/* ── How it works ── */}
      <HowItWorks />
    </div>
  );
}

function DetectorStatus({ status }) {
  const configs = {
    active:   { color: '#27ae60', label: '● Detector Active',  bg: '#eafaf1' },
    warming:  { color: '#e67e22', label: '⏳ Warming Up (24h)', bg: '#fef5e7' },
    error:    { color: '#c0392b', label: '✗ Connection Error',  bg: '#fdecea' },
    checking: { color: '#888',    label: '… Checking',          bg: '#f9f9f9' },
  };
  const cfg = configs[status] || configs.checking;
  return (
    <div style={{ background: cfg.bg, color: cfg.color, padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
      {cfg.label}
    </div>
  );
}

function MetricHealthStrip({ alerts }) {
  const metrics = ['CycleTime', 'Temperature', 'ClampForce', 'OEE'];
  const alertMap = {};
  alerts.forEach(a => {
    if (!alertMap[a.metric] || _sevOrder(a.severity) > _sevOrder(alertMap[a.metric].severity)) {
      alertMap[a.metric] = a;
    }
  });

  return (
    <div style={s.healthStrip}>
      {metrics.map(m => {
        const alert = alertMap[m];
        const cfg   = alert ? SEV_CONFIG[alert.severity] : { bg: '#eafaf1', color: '#27ae60', border: '#27ae60' };
        return (
          <div key={m} style={{ ...s.healthCard, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
            <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{m}</span>
            <span style={{ fontSize: 20 }}>{alert ? SEV_CONFIG[alert.severity]?.icon : '✅'}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>
              {alert ? alert.severity : 'NORMAL'}
            </span>
            {alert?.value && <span style={{ fontSize: 11, color: '#888' }}>{alert.value}</span>}
          </div>
        );
      })}
    </div>
  );
}

function AlertCard({ alert, resolved }) {
  const cfg = SEV_CONFIG[alert.severity] || SEV_CONFIG.LOW;
  const src = SOURCE_LABELS[alert.source] || { label: alert.source, icon: '📊' };
  const age = _formatAge(alert.createdAt);

  return (
    <div style={{
      ...s.alertCard,
      background: resolved ? '#f9f9f9' : cfg.bg,
      borderLeft: `4px solid ${resolved ? '#ccc' : cfg.border}`,
      opacity: resolved ? 0.75 : 1,
    }}>
      <div style={s.alertTop}>
        <div style={s.alertLeft}>
          <span style={{ fontSize: 18 }}>{cfg.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: resolved ? '#888' : cfg.color }}>
              {alert.metric}
              {alert.anomalyType && <span style={s.typeTag}>{alert.anomalyType.replace('_', ' ')}</span>}
            </div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{alert.description}</div>
          </div>
        </div>
        <div style={s.alertRight}>
          <span style={{ ...s.sevBadge, background: resolved ? '#eee' : cfg.border }}>
            {alert.severity}
          </span>
          <span style={s.srcBadge}>{src.icon} {src.label}</span>
          {alert.score != null && (
            <span style={{ fontSize: 11, color: '#888' }}>Score: {alert.score}</span>
          )}
        </div>
      </div>
      <div style={s.alertMeta}>
        <span>🕐 {age}</span>
        {alert.value && <span>📊 Value: {alert.value}</span>}
        <span style={{ color: resolved ? '#27ae60' : '#e67e22' }}>
          {resolved ? '✅ Resolved' : '🔴 Active'}
        </span>
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <div style={{ ...s.section, background: '#f0f4f8', borderRadius: 8, padding: 20, marginTop: 24 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>ℹ️ How AI Anomaly Detection Works</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {[
          ['🤖 ML Detection', 'Statistical Z-score engine learns normal patterns from 7 days of SiteWise history, then flags values beyond 2.5 standard deviations automatically'],
          ['📏 Rule Engine', 'Immediate alerts when values cross hard thresholds: Temperature > 90°C or -999 (sensor fault), ClampForce < 1200N, CycleTime > 25s, OEE < 30%'],
          ['📈 Trend Analysis', 'Detects consistent drift — 5 consecutive moves in same direction with >15% total change triggers a trend alert'],
          ['📬 Alerting', 'SiteWise polled every 5 minutes via EventBridge. Anomalies stored in DynamoDB and shown here with auto-resolve when metrics return to normal'],
        ].map(([title, desc]) => (
          <div key={title} style={{ background: '#fff', borderRadius: 6, padding: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function _formatAge(createdAt) {
  if (!createdAt) return 'unknown';
  const diffMs  = Date.now() - createdAt;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)  return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}

function _sevOrder(sev) {
  return { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 }[sev] || 0;
}

const s = {
  page:        { maxWidth: 1100, margin: '0 auto', fontFamily: 'sans-serif' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  heading:     { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub:         { color: '#666', fontSize: 13, margin: 0 },
  statusRow:   { display: 'flex', gap: 10, alignItems: 'center' },
  refreshBtn:  { padding: '7px 14px', background: '#f0f4f8', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  errorBox:    { background: '#fdecea', color: '#c0392b', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 },
  summaryRow:  { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
  summaryCard: { borderRadius: 8, padding: '14px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 90, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  pollInfo:    { display: 'flex', flexDirection: 'column', gap: 2, marginLeft: 'auto' },
  healthStrip: { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' },
  healthCard:  { borderRadius: 8, padding: '12px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 110, flex: 1 },
  section:     { marginBottom: 24 },
  sectionTitle:{ fontSize: 16, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 },
  badge:       { background: '#e74c3c', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 12 },
  empty:       { color: '#888', textAlign: 'center', padding: 32 },
  allClear:    { background: '#eafaf1', border: '1px solid #27ae60', borderRadius: 8, padding: 24, display: 'flex', alignItems: 'center', gap: 12, color: '#1e8449', fontSize: 15, fontWeight: 600 },
  alertList:   { display: 'flex', flexDirection: 'column', gap: 10 },
  alertCard:   { borderRadius: 8, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  alertTop:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  alertLeft:   { display: 'flex', gap: 10, alignItems: 'flex-start', flex: 1 },
  alertRight:  { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  alertMeta:   { display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: '#888' },
  sevBadge:    { color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 },
  srcBadge:    { background: '#f0f4f8', color: '#555', padding: '2px 8px', borderRadius: 10, fontSize: 11 },
  typeTag:     { background: '#eee', color: '#555', padding: '1px 6px', borderRadius: 4, fontSize: 10, marginLeft: 6, fontWeight: 400 },
};
